import { clientFilterText } from '../../components/clientNames';
import {
  CreateButton,
  DeleteButton,
  EditButton,
  List,
  SaveButton,
  useModal,
} from '@refinedev/antd';

import {
  Button,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Skeleton,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd';

import {
  IResourceComponentsProps,
  useCustomMutation,
  useGetIdentity,
  useLink,
  useInvalidate,
  useNavigation,
  useNotification,
  useTranslate,
} from '@refinedev/core';

import {
  ApartmentOutlined,
  ContainerOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  QuestionCircleOutlined,
  UsergroupDeleteOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { ACCESS_LEVELS, Client, User, UserRole } from '@cc/schema';
import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { ClientsLimitReached } from '../../components/ClientsLimitReached';
import { SubscriptionExpired } from '../../components/SubscriptionExpired';
import { SubscriptionExpiringSoon } from '../../components/SubscriptionExpiringSoon';
import { useCcEditableTable } from '../../helpers';
import { useSubscriptionWarnings } from '../../hooks';
import { useClientOptions } from '../../hooks/client-options';
import { IUser } from '../../providers';
import { colors } from '../../styles/colors';
import { resetCachedDbUser } from '../../providers/firebaseAuthNew';
import { TransferClientAction } from './TransferClientAction';
import useStyles from './styles.style';

const data = [
  '_id',
  'firstName',
  'lastName',
  'email',
  'accessLevel',
  'esMode', // ES coexistence marker (plan 07 §4 C3)
  'createdAt',
  'role',
  'diagnoses',
  {
    access: ['allowView', 'allowEdit', 'allowAdminView', 'allowAdminEdit'],
  },
  {
    activeSubscriptions: [
      {
        data: [
          '_id',
          'startsAt',
          'expiresAt',
          'type',
          'isActive',
          'practiceId',
        ],
      },
    ],
  },
];
const metaData = {
  fields: [
    {
      data,
    },

    'count',
  ],
};

/** The search box waits this long after the last keystroke before it asks the server again. */
const SEARCH_DEBOUNCE_MS = 300;

export const ClientList: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();

  const { styles } = useStyles();

  const Link = useLink();
  const { push } = useNavigation();
  const [obfuscate, setObfuscate] = useState(true);
  const [isPremiumByPracticeSet, setIsPremiumByPracticeSet] = useState(false);

  const [selectedMergeClientId, setSelectedMergeClientId] = useState<string>();

  /**
   * #5 (founder 2026-10-03, 1.5.0 manual pass) — ONE search box. What is typed matches any part of a
   * client's first name, last name, "first last", e-mail or id; it is a QUERY argument
   * (`Query.clients(…, search)`), not a browser filter, because the list is paginated. Typing waits
   * 300 ms, then the table goes back to page 1 and asks again; `count`, the page numbers and "x of y"
   * follow the filtered set. It ANDs with the column filters and sorters.
   */
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const handle = setTimeout(
      () => setSearch(searchInput.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
  }, [searchInput]);
  const listMeta = useMemo(
    () =>
      search
        ? {
            ...metaData,
            variables: {
              search: { name: 'search', type: 'String', value: search },
            },
          }
        : metaData,
    [search],
  );

  const {
    modalProps: { visible, ...modalProps },
    show,
    close,
  } = useModal();
  const {
    modalProps: { visible: visible1, ...modalPropsExpired },
    show: showExpired,
    close: closeExpired,
  } = useModal();
  const {
    modalProps: { visible: visible2, ...modalPropsLimitReached },
    show: showLimitReached,
    close: closeLimitReached,
  } = useModal();

  const [nonAppClientIdToMerge, setNonAppClientIdToMerge] = useState<string>();
  const {
    modalProps: { visible: visible3, ...modalPropsMergeAccounts },
    show: showMergeAccounts,
    close: closeMergeAccounts,
  } = useModal();

  const { clientsLoading, memoizedClientsOptions } = useClientOptions();

  const {
    data: identity,
    remove,
    refetch: refetchIdentity,
  } = useGetIdentity<IUser>();

  const { replace } = useNavigation();

  /**
   * Re-read the signed-in user — and with it the practice's stored seat warnings, the banner — AFTER
   * a write has answered (R105, the founder's prod pass 2026-09-23: "the counter did not update, I
   * had to reload"). The page used to re-read on a fixed 500 ms timer started at the CLICK, which
   * raced the mutation itself; the server now re-counts the seats before it answers, so reading
   * once the answer is in shows the new number.
   *
   * …and every cached CLIENT LIST with it (R113, the founder's A4 the same morning: "I added a
   * non-app client and could not find the client in the Filter by Client, though saw an already
   * deleted one there. After refreshing the page it appeared."). The header's filter reads the
   * shared roster (`useClientRoster`, its own 200-row `clients` query); this page re-read only its
   * table, so the roster kept whoever was there at load — new clients missing, deleted ones offered.
   */
  const invalidate = useInvalidate();
  const refreshAfterWrite = () => {
    invalidate({ resource: 'clients', invalidates: ['list'] });
    resetCachedDbUser();
    return refetchIdentity();
  };

  const subWarnings = useSubscriptionWarnings();

  const { open } = useNotification();

  // a new search starts at the first page of the filtered set
  useEffect(() => {
    setCurrent(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (!obfuscate) {
      const handle = setTimeout(() => {
        setObfuscate(true);
      }, 1000 * 60);
      return () => clearTimeout(handle);
    }
  }, [obfuscate]);

  const showUpgradeSubscriptionError = () => {
    open?.({
      type: 'error',
      message: '',
      description: translate('clients.upgradeWarning'),
    });
  };

  const nonAppClientInitialValues = {
    firstName: '',
    lastName: '',
    email: '',
  };

  const [nonAppClientForm] = Form.useForm<typeof nonAppClientInitialValues>();

  const {
    table: { tableProps, tableQueryResult, setCurrent },
    form: { formProps },
    isEditing,
    saveButtonProps,
    cancelButtonProps,
    editButtonProps,
  } = useCcEditableTable<Client>(
    {
      resource: 'clients',
      queryOptions: {
        select: (data) => {
          // @ts-ignore
          return { data: data.data.data, total: data.total };
        },
      },
      pagination: {
        pageSize: subWarnings.limitReached
          ? subWarnings.clientsPurchased * 4
          : 10, // default
      },
      filters: {
        defaultBehavior: 'replace',
      },
      sorters: {
        initial: [
          {
            field: '_id',
            order: 'desc',
          },
        ],
      },
      meta: listMeta,
    },
    {
      onMutationSuccess: () => {
        cancelButtonProps.onClick();
        refreshAfterWrite();
      },
      meta: {
        fields: data,
      },
    },
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const { mutateAsync: createClient } = useCustomMutation<User>();
  const { mutateAsync: mergeNonAppClient } = useCustomMutation<User>();

  const handleOk = async () => {
    setConfirmLoading(true);
    const values = nonAppClientForm.getFieldsValue();
    try {
      await createClient({
        url: '',
        method: 'post',
        values: {},
        meta: {
          operation: 'createNonAppClient',
          fields: ['_id'],
          variables: {
            input: {
              name: 'input',
              type: 'CreateNonAppClientInput!',
              value: {
                ...values,
                email: values?.email?.toLowerCase() || null,
              },
            },
          },
        },
      });

      nonAppClientForm.resetFields();

      await tableQueryResult.refetch();
      await refreshAfterWrite();

      setIsModalOpen(false);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };
  useEffect(() => {
    if (
      // @ts-ignore
      subWarnings.clientsPurchased !== tableProps?.pagination?.pageSize
    ) {
      // @ts-ignore
      tableProps?.onChange?.(
        { current: 1, pageSize: subWarnings.clientsPurchased * 4 },
        // @ts-ignore
        null,
        null,
        null,
      );
    }
  }, [subWarnings.clientsPurchased]);

  return (
    <List
      title={translate('clients.clients')}
      headerProps={{
        extra: (
          <Space wrap>
            <Input.Search
              allowClear
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onSearch={(value) => setSearch(value.trim())}
              placeholder={translate('clients.search.placeholder')}
              style={{ width: 280 }}
              data-testid="cc-clients-list-search"
            />
            <div style={{ marginRight: 30 }}>
              <Tooltip title={translate('clients.piiTip')}>
                <QuestionCircleOutlined style={{ marginRight: 5 }} />
              </Tooltip>
              <Button
                icon={obfuscate ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                onClick={() => setObfuscate(!obfuscate)}
                data-testid="cc-clients-list-toggle-obfuscate"
              >
                {translate(obfuscate ? 'clients.showInfo' : 'clients.hideInfo')}
              </Button>
            </div>
            <div>
              <Tooltip title={translate('clients.nonAppClientDescription')}>
                <QuestionCircleOutlined style={{ marginRight: 5 }} />
              </Tooltip>
              <CreateButton
                data-testid="add-non-app-client"
                disabled={
                  subWarnings?.limitReached || subWarnings?.subscriptionExpired
                }
                onClick={() => {
                  setIsModalOpen(true);
                }}
              >
                {translate('clients.addNonAppClient')}
              </CreateButton>
            </div>
            <CreateButton
              disabled={
                subWarnings?.limitReached || subWarnings?.subscriptionExpired
              }
              onClick={() => {
                push('/invites/create?type=1');
              }}
            >
              {translate('clients.inviteAppClient')}
            </CreateButton>
            <ClientsLimitReached />
            <SubscriptionExpiringSoon />
            <SubscriptionExpired />

            <Modal
              title={
                <>
                  <>{translate('clients.addNonAppClient')}</>
                  <Tooltip title={translate('clients.nonAppClientDescription')}>
                    <QuestionCircleOutlined style={{ marginLeft: 10 }} />
                  </Tooltip>
                </>
              }
              open={isModalOpen}
              onOk={handleOk}
              confirmLoading={confirmLoading}
              onCancel={handleCancel}
            >
              <Form<typeof nonAppClientInitialValues>
                data-testid="add-non-app-client-modal"
                form={nonAppClientForm}
                initialValues={{
                  ...nonAppClientInitialValues,
                }}
                onFinish={(values) => {}}
                // onValuesChange={onValuesChange}
                labelCol={{ span: 7 }}
                wrapperCol={{ span: 16 }}
                labelWrap
              >
                <Form.Item
                  label={translate('invites.fields.firstName')}
                  name="firstName"
                  required
                >
                  <Input data-testid="clients-add-non-app-first-name" />
                </Form.Item>

                <Form.Item
                  label={translate('invites.fields.lastName')}
                  name="lastName"
                  required
                >
                  <Input data-testid="clients-add-non-app-last-name" />
                </Form.Item>

                <Form.Item
                  label={translate('invites.fields.email')}
                  name="email"
                >
                  <Input type="email" data-testid="clients-add-non-app-email" />
                </Form.Item>
              </Form>
            </Modal>
          </Space>
        ),
      }}
    >
      <Form {...formProps}>
        <Table
          {...tableProps}
          data-testid="clients-table"
          rowKey="_id"
          locale={
            search
              ? {
                  // the antd empty state (as every empty table shows), with the search's own line
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={translate('clients.search.empty')}
                    />
                  ),
                }
              : undefined
          }
          onChange={(pagination, filters, sorter, extra) => {
            if (subWarnings.limitReached) {
              showUpgradeSubscriptionError();
              return;
            }
            // Note: The data we get from Ant.design filters does not match the
            // react refine one. Currently it works fine if I simply silence it the way below.
            // TOOD: Find a way to convert the data to match the react refine one and fix it better
            // @ts-ignore
            tableProps?.onChange?.(pagination, null, sorter, extra);
          }}
          pagination={{
            ...tableProps.pagination,
            showSizeChanger: true,
          }}
        >
          <Table.Column
            key="_id"
            dataIndex="_id"
            title={translate('clients.fields.id')}
            sorter
            filterSearch
            // @ts-ignore
            onFilter={(value: string, record: User) => {
              return record._id === value;
            }}
            filters={
              // @ts-ignore
              tableProps.dataSource?.map((c, idx) => {
                return {
                  text: clientFilterText(c, !obfuscate),
                  value: c._id,
                };
              })
            }
            render={(value, record) => {
              const client = tableProps.dataSource?.find?.(
                (c) => c._id === value,
              );

              return (
                <Tooltip
                  title={client ? `${client.firstName} ${client.lastName}` : ''}
                >
                  {`${value.replace('users/', '')} - ${(
                    client?.firstName || ''
                  ).charAt(0)}${(client?.lastName || '').charAt(0)}`}
                </Tooltip>
              );
            }}
          />
          <Table.Column<Client>
            key="firstName"
            dataIndex="firstName"
            title={translate('clients.fields.firstName')}
            sorter
            render={(value, record) => {
              if (isEditing(record._id)) {
                return (
                  <Form.Item name={['firstName']} style={{ marginBottom: 0 }}>
                    <Input />
                  </Form.Item>
                );
              }
              return obfuscate ? (
                <Skeleton
                  active
                  paragraph={false}
                  className={styles['client-list-skeleton']}
                />
              ) : (
                value
              );
            }}
          />
          <Table.Column<Client>
            key="lastName"
            dataIndex="lastName"
            title={translate('clients.fields.lastName')}
            sorter
            render={(value, record) => {
              if (isEditing(record._id)) {
                return (
                  <Form.Item name={['lastName']} style={{ marginBottom: 0 }}>
                    <Input />
                  </Form.Item>
                );
              }
              return obfuscate ? (
                <Skeleton
                  active
                  paragraph={false}
                  className={styles['client-list-skeleton']}
                />
              ) : (
                value
              );
            }}
          />
          <Table.Column<Client>
            key="email"
            dataIndex="email"
            title={translate('clients.fields.email')}
            sorter
            filterSearch
            // @ts-ignore
            onFilter={(value: string, record: User) => {
              return record.email === value;
            }}
            filters={
              // @ts-ignore
              tableProps.dataSource?.map((c, idx) => {
                return {
                  text: c.email,
                  value: c.email,
                };
              })
            }
            render={(value: string, record) => {
              const normalizedValue =
                record?.role === UserRole.NON_MOBILE
                  ? value?.startsWith('cc-') &&
                    value?.endsWith('@copingcard.com')
                    ? ''
                    : value?.replace('cc-', '')
                  : value;

              return obfuscate ? (
                <Skeleton
                  active
                  paragraph={false}
                  className={styles['client-list-skeleton']}
                />
              ) : (
                normalizedValue
              );
            }}
          />
          <Table.Column<Client>
            key="esMode"
            dataIndex="esMode"
            title={
              <>
                {translate('clients.newExperience')}{' '}
                <Tooltip title={translate('clients.newExperienceTip')}>
                  <QuestionCircleOutlined style={{ marginLeft: '5px' }} />
                </Tooltip>
              </>
            }
            render={(value) =>
              value === 'es' ? (
                <Tag color="green" data-testid="client-new-experience">
                  {translate('clients.newExperience')}
                </Tag>
              ) : null
            }
          />
          <Table.Column<Client>
            key="accessLevel"
            dataIndex="accessLevel"
            title={
              <>
                {translate('clients.fields.accessLevel')} {''}{' '}
                <Tooltip
                  title={
                    <>
                      {translate('clients.freeTip')}
                      <br />
                      {translate('clients.premiumTip')}
                      <br />
                      {translate('clients.premiumPracticeTip')}
                      <br />
                      {translate('clients.nonAppClientTip')}
                    </>
                  }
                >
                  <QuestionCircleOutlined style={{ marginLeft: '5px' }} />
                </Tooltip>{' '}
              </>
            }
            render={(value, record) => {
              if (
                record.role !== UserRole.NON_MOBILE &&
                isEditing(record._id)
              ) {
                const options =
                  value === ACCESS_LEVELS.premium ||
                  (value === ACCESS_LEVELS.premium_practice &&
                    record.activeSubscriptions?.data?.find(
                      (s) => s.practiceId !== identity?.db?.practiceId,
                    ))
                    ? [
                        {
                          label: translate(`clients.premium_practice`),
                          value: ACCESS_LEVELS.premium_practice,
                        },
                        {
                          label: translate(`clients.premium`),
                          value: ACCESS_LEVELS.premium,
                        },
                      ]
                    : [
                        {
                          label: translate(`clients.premium_practice`),
                          value: ACCESS_LEVELS.premium_practice,
                        },
                        {
                          label: translate(`clients.free`),
                          value: null,
                        },
                      ];

                return (
                  <Form.Item
                    name={['accessLevel']}
                    style={{ marginBottom: 0 }}
                    getValueProps={(value) => {
                      const valueToRender =
                        !isPremiumByPracticeSet &&
                        value === ACCESS_LEVELS.premium_practice &&
                        !record.activeSubscriptions?.data?.find(
                          (s) => s.practiceId === identity?.db?.practiceId,
                        ) &&
                        record.activeSubscriptions?.data?.find(
                          (s) => s.practiceId !== identity?.db?.practiceId,
                        )
                          ? ACCESS_LEVELS.premium
                          : value;
                      return {
                        value: valueToRender,
                      };
                    }}
                  >
                    <Select
                      // @ts-ignore
                      options={options}
                      onChange={(value) => {
                        if (value === ACCESS_LEVELS.premium_practice) {
                          if (subWarnings.limitReached) {
                            showLimitReached();
                            return;
                          }
                          if (subWarnings.subscriptionExpired) {
                            showExpired();
                            return;
                          }
                          setIsPremiumByPracticeSet(true);
                        } else {
                          setIsPremiumByPracticeSet(false);
                        }
                        if (value === null) {
                          show();
                        }
                      }}
                      style={{ width: 250 }}
                    />
                  </Form.Item>
                );
              }

              const valueToRender =
                !isPremiumByPracticeSet &&
                value === ACCESS_LEVELS.premium_practice &&
                !record.activeSubscriptions?.data?.find(
                  (s) => s.practiceId === identity?.db?.practiceId,
                ) &&
                record.activeSubscriptions?.data?.find(
                  (s) => s.practiceId !== identity?.db?.practiceId,
                )
                  ? ACCESS_LEVELS.premium
                  : value;

              return translate(
                record.role === UserRole.NON_MOBILE
                  ? 'clients.premiumNonApp'
                  : valueToRender === ACCESS_LEVELS.premium_practice
                  ? `clients.premium_practice`
                  : valueToRender === ACCESS_LEVELS.premium
                  ? `clients.premium`
                  : 'clients.free',
              );
            }}
          />
          <Table.Column
            key="access"
            dataIndex="access"
            title={translate('clients.fields.access')}
            render={(value, record) => {
              if (!value) {
                return <EyeInvisibleOutlined />;
              }
              /**
               * THE CLIENT REVOKED ACCESS (founder ruling 2026-09-19, P020). A relation with all four
               * flags off is a client who ended the sharing; the row stays — RTM, legal and
               * administrative reasons — and says so, instead of looking like a client who never
               * shared. A reconnect flips this same relation back and the label goes.
               */
              if (
                !value.allowView &&
                !value.allowEdit &&
                !value.allowAdminView &&
                !value.allowAdminEdit
              ) {
                return (
                  <Tag
                    color="default"
                    data-testid="cc-clients-list-access-revoked"
                  >
                    {translate('clients.fields.accessRevoked')}
                  </Tag>
                );
              }
              return (
                <div>
                  {value.allowView && <EyeOutlined />}
                  {value.allowEdit && (
                    <EditOutlined style={{ marginLeft: '5px' }} />
                  )}
                </div>
              );
            }}
          />
          <Table.Column
            key="createdAt"
            dataIndex="createdAt"
            title={translate('clients.fields.createdAt')}
            sorter
            render={(text, record, indx) => {
              if (!text) return undefined;
              const date = new Date(text);
              return format(date, 'yyyy-MM-dd');
            }}
          />
          <Table.Column<Client>
            title={translate('resourcesCommon.actions')}
            dataIndex="actions"
            render={(_, record) => {
              if (isEditing(record._id)) {
                return (
                  <Space wrap>
                    <SaveButton
                      {...saveButtonProps}
                      onClick={async () => {
                        setIsPremiumByPracticeSet(false);
                        await saveButtonProps.onClick();
                      }}
                      size="small"
                    />
                    <Button {...cancelButtonProps} size="small">
                      {translate('buttons.cancel')}
                    </Button>
                  </Space>
                );
              }
              const canEdit =
                record?.access?.allowEdit ||
                (record?.access?.allowAdminEdit &&
                  identity?.db?.role === UserRole.THERAPIST_ADMIN);

              const canDelete = record.role === UserRole.NON_MOBILE;

              const showDisabled = !(
                record?.access?.allowView ||
                (identity?.db?.role === UserRole.THERAPIST_ADMIN &&
                  record?.access?.allowAdminView)
              );

              return (
                <Space wrap>
                  {canEdit && (
                    <Tooltip
                      title={translate(
                        record?.accessLevel
                          ? 'clients.thoughtsTest'
                          : 'clients.thoughtsTestDisabledTip',
                      )}
                    >
                      <Link
                        to={`/tools/portalThoughts/create?clientId=${record._id}`}
                      >
                        <Button
                          icon={<ContainerOutlined />}
                          disabled={!record?.accessLevel}
                        />
                      </Link>
                    </Tooltip>
                  )}

                  <Tooltip title={translate('resourcesCommon.edit')}>
                    <div>
                      <EditButton
                        {...editButtonProps(record._id)}
                        hideText
                        data-testid="cc-clients-list-edit-button"
                      />
                    </div>
                  </Tooltip>

                  <Tooltip title={translate('resourcesCommon.view')}>
                    <Link
                      to={`/clients/show/${encodeURIComponent(record._id)}`}
                    >
                      <Button
                        icon={<EyeOutlined />}
                        disabled={showDisabled}
                        data-testid="cc-clients-list-show-button"
                      />
                    </Link>
                  </Tooltip>

                  {canEdit && (
                    <Tooltip
                      title={translate(
                        record?.accessLevel
                          ? 'pages.conceptualization.title'
                          : 'clients.thoughtsTestDisabledTip',
                      )}
                    >
                      <Link
                        to={`/tools/conceptualizations/create?clientId=${record._id}`}
                      >
                        <Button
                          icon={<ApartmentOutlined />}
                          disabled={!record?.accessLevel}
                        />
                      </Link>
                    </Tooltip>
                  )}
                  {/* E07 · ask this client to move to a colleague. THERAPIST_ADMIN only: the
                      server refuses anybody else, and an affordance that always fails is worse
                      than none. Offered for app clients only — a non-app client has nobody to
                      answer the request, and `reassignClient` would wait for a consent that can
                      never arrive. */}
                  {identity?.db?.role === UserRole.THERAPIST_ADMIN &&
                    record.role !== UserRole.NON_MOBILE && (
                      <TransferClientAction
                        subject={record._id}
                        clientLabel={
                          [record.firstName, record.lastName]
                            .filter(Boolean)
                            .join(' ') ||
                          record.email ||
                          record._id
                        }
                        excludeAssignees={
                          identity?.db?._id ? [identity.db._id] : undefined
                        }
                      />
                    )}
                  {canDelete && (
                    <Tooltip title={translate('clients.mergeTip')}>
                      <Button
                        data-testid="client-merge-button"
                        icon={<UsergroupDeleteOutlined />}
                        onClick={() => {
                          setNonAppClientIdToMerge(record._id);
                          showMergeAccounts();
                        }}
                      />
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title={translate('clients.deleteTip')}>
                      <DeleteButton
                        data-testid="client-delete-button"
                        resource="clients"
                        meta={{
                          fields: ['_id'],
                        }}
                        onSuccess={() => {
                          refreshAfterWrite();
                        }}
                        hideText
                        size="small"
                        recordItemId={record._id}
                        confirmTitle={translate('clients.deleteConfirmText')}
                        confirmOkText={translate('clients.deleteOkText')}
                        confirmCancelText={translate(
                          'clients.deleteCancelText',
                        )}
                      />
                    </Tooltip>
                  )}
                </Space>
              );
            }}
          />
        </Table>
      </Form>

      <Modal
        {...modalProps}
        maskClosable={false}
        title={translate('clients.freeDowngradeWarning')}
        onOk={() => {
          close();
        }}
        closable={false}
        cancelButtonProps={{ style: { display: 'none' } }}
      />

      <Modal
        {...modalPropsExpired}
        maskClosable={false}
        title={translate('clients.expiredWarning')}
        onOk={() => {
          closeExpired();
        }}
        closable={false}
        cancelButtonProps={{ style: { display: 'none' } }}
      />

      <Modal
        {...modalPropsLimitReached}
        maskClosable={false}
        title={translate('clients.limitReachedWarning')}
        onOk={() => {
          closeLimitReached();
        }}
        closable={false}
        cancelButtonProps={{ style: { display: 'none' } }}
      />
      <Modal
        {...modalPropsMergeAccounts}
        maskClosable={false}
        data-testid="cc-clients-merge-modal"
        title={translate('clients.mergeTip')}
        onOk={async () => {
          const id = selectedMergeClientId;

          await mergeNonAppClient({
            url: '',
            method: 'post',
            values: {},
            meta: {
              operation: 'mergeClients',
              fields: ['_id'],
              variables: {
                input: {
                  name: 'input',
                  type: 'MergeClientsInput!',
                  value: {
                    nonAppClientId: nonAppClientIdToMerge,
                    appClientId: id,
                  },
                },
              },
            },
          });

          closeMergeAccounts();
          setSelectedMergeClientId(undefined);
          setNonAppClientIdToMerge(undefined);

          await tableQueryResult.refetch();
          await refreshAfterWrite();
        }}
        onCancel={() => {
          setSelectedMergeClientId(undefined);
          setNonAppClientIdToMerge(undefined);
          closeMergeAccounts();
        }}
      >
        <div>
          <div>
            <WarningOutlined
              color="yellow"
              style={{
                fontSize: 20,
                marginRight: 5,
                color: colors['--primary-0'],
              }}
            />

            {translate('clients.mergeWarning')}
          </div>

          <br />
          <div>{translate('clients.mergeTipDetails')}</div>
          <br />
          <div
            style={{
              fontWeight: 'bold',
              marginBottom: 5,
            }}
          >
            {translate('clients.mergeLabel')}:
          </div>
          <Select
            style={{
              minWidth: 300,
            }}
            showSearch
            data-testid="cc-clients-merge-select"
            value={selectedMergeClientId}
            placeholder={translate('clients.selectMergeAppClient')}
            optionFilterProp="children"
            loading={clientsLoading}
            onChange={setSelectedMergeClientId}
            filterOption={(input, option) =>
              (option!.children as unknown as string)
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {memoizedClientsOptions}
          </Select>
        </div>
      </Modal>
    </List>
  );
};
